import {useDispatch, useSelector} from 'react-redux';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import type {AppState, Job} from '@jobseeker/common-core/types';
import Loader from '@jobseeker/lotus/components/Loader';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {Box, Card, Skeleton, Typography} from '@mui/material';
import {useState} from 'react';
import JobList from '@jobseeker/lotus/components/JobList';
import dayjs from 'dayjs';
import InfiniteScroll from '@jobseeker/lotus/components/InfiniteScroll';
import {additionalInfoIconMap} from '@jobseeker/feature-job/components/JobFeed/V2';
import kebabCase from 'lodash/kebabCase';
import LoadingButton from '@mui/lab/LoadingButton';
import NoJobsSaved from '@jobseeker/feature-job/components/NoSavedJobs';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';

type Fn = (event: Event, target: HTMLElement) => void;
type ListWithTitleProps = {
  routeToJobDetail: (jobSlug: string, jobId: string | number) => void;
  onLoadMore: Fn;
  jobInfoKeyElements: any;
  feedOnly: boolean;
  title: string;
  subTitle?: string;
  showThreeDotMenu?: boolean;
  showSaveIcon?: boolean;
  onInfiniteScroll?: () => void;
  variant?: string;
  injectAsChild?: boolean;
  infiniteScroll?: boolean;
  onJobSave?: (jobData: Job, unsave: boolean) => void;
  isSavedJobPage: boolean;
};

const ListWithTitle = ({
  routeToJobDetail,
  onLoadMore,
  jobInfoKeyElements,
  feedOnly,
  title,
  onInfiniteScroll,
  injectAsChild,
  subTitle,
  showSaveIcon,
  showThreeDotMenu,
  infiniteScroll,
  variant,
  onJobSave,
  isSavedJobPage,
  ...props
}: ListWithTitleProps) => {
  const [loadMoreLoading, setLoadMoreLoading] = useState<boolean>(false);
  const dispatch = useDispatch();

  useValidateAuth(dispatch);

  const jobFeedStore = useSelector((state: AppState) => state.job);

  const onUnsave = (jobDetail: Job, action: boolean) => {
    dispatch({
      type: 'job/jobFeed/hide_job',
      payload: jobDetail.id,
    });
    onJobSave && onJobSave(jobDetail, true);
  };

  const onLoadFeed = () => {
    setLoadMoreLoading(true);
    if (onLoadMore) {
      if (!injectAsChild) {
        onLoadMore();
      }
    }
    setTimeout(() => {
      setLoadMoreLoading(false);
    }, 1000);
  };

  const onJobClick = (job: Job) => {
    const slug = kebabCase(job.title.replace(/&/g, '-and-'));
    routeToJobDetail(slug, job.id);
  };

  const ContainerComponent = infiniteScroll ? InfiniteScroll : Box;
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
  const isLarge = useIsLargeDevice(425);
  return (
    <Card
      sx={{
        boxShadow: !isAppIimjobs ? 0 : 2,
        borderRadius: 4,
        mx: isAppIimjobs && showThreeDotMenu ? {lg: 12.5, xl: 17.5} : 0,
        bgcolor: !isAppIimjobs ? '#f2f5fa' : '',
        mt: !isAppIimjobs ? (!isLarge ? -2 : '') : '',
      }}
    >
      <Box>
        <Typography
          component={injectAsChild ? 'p' : 'h1'}
          display="flex"
          fontSize={!isAppIimjobs ? 20 : {xxs: 16, md: 20}}
          fontWeight={!isAppIimjobs ? 700 : {xxs: 600, md: 500}}
          m={2}
          mt={!isAppIimjobs ? 0 : 3}
          sx={{
            wordWrap: 'break-word',
            textTransform: 'capitalize',
          }}
        >
          {title}
        </Typography>
        {isAppIimjobs && subTitle && (
          <Typography
            component={injectAsChild ? 'p' : 'h1'}
            fontSize={12}
            fontWeight={500}
            m={2}
            mt={-2}
            mb={3}
            display="flex"
            color="text.secondary"
            sx={{
              wordWrap: 'break-word',
            }}
          >
            {subTitle}
          </Typography>
        )}
        {jobFeedStore?.jobfeed.length > 0 ? (
          <ContainerComponent
            //key={`${Date.now()}`}
            dataLength={jobFeedStore?.jobfeed?.length}
            next={onLoadMore}
            hasMore={!!jobFeedStore.loadMoreEnabled}
            onScroll={onInfiniteScroll}
            loader={
              <Box
                sx={{
                  display: 'flex',
                  textAlign: 'center',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Loader />
              </Box>
            }
            style={{
              scrollbarWidth: 'none',
              paddingTop: !isAppIimjobs ? '8px' : 0,
            }}
          >
            {jobFeedStore?.jobfeed?.map((job: Job | any, index: number) => {
              const additionalInfo: any[] = [];
              jobInfoKeyElements.forEach(
                (key: {value: string; label: string}) => {
                  const {label, value} = key;
                  if (job[label]) {
                    additionalInfo.push({
                      label: value,
                      // value,
                      icon: additionalInfoIconMap[label],
                    });
                  }
                }
              );

              return (
                <Box
                  key={`${job.id}`}
                  mb={!isAppIimjobs ? '10px' : 0}
                  mx={!isAppIimjobs ? (isLarge ? 2 : 1) : 0}
                  data-testid="job-list"
                >
                  <JobList
                    noShadow
                    // key={`${index}${Date.now()}`}
                    title={job?.title
                      ?.split('(')[0]
                      // ?.replace(/\//gm, '/ ')
                      .trim()}
                    refCode={title === 'Saved Jobs' ? 'sj' : job.refCode}
                    experiance={`${job.min} - ${job.max} yrs`}
                    companyName={job.companyData.companyName}
                    date={dayjs(job.createdTime).toDate()}
                    location={
                      job?.location.length > 3
                        ? 'Multiple Locations'
                        : job?.location?.map((l: any) => l.name).join('/')
                            .length > 30
                        ? 'Multiple Locations'
                        : job?.location?.map((l: any) => l.name).join('/')
                    }
                    variant={
                      !isAppIimjobs
                        ? VersionVariants.VERSION_SEVEN
                        : VersionVariants.VERSION_TWO
                    }
                    isCompanyJob={Number(job?.id) % 2 === 0}
                    jobData={job}
                    jobPos={index+1}
                    showSaveIcon={showSaveIcon}
                    showThreeDotMenu={showThreeDotMenu}
                    onJobClick={() => onJobClick(job)}
                    additionalInfo={additionalInfo}
                    isPremiumJob={!!job.premium}
                    isSaved={!!job.saved}
                    onSaveClick={onUnsave}
                    companyImgUrl={
                      job.recruiter?.centralizedCompanyId
                        ? `${process.env.NEXT_PUBLIC_S3_COMPANY_LOGO_BUCKET}${job.recruiter?.centralizedCompanyId}.webp`
                        : ''
                    }
                    fallbackCompanyUrl={
                      job.recruiter?.centralizedCompanyId
                        ? `${process.env.NEXT_PUBLIC_S3_COMPANY_LOGO_BUCKET}${job.recruiter?.centralizedCompanyId}.webp`
                        : ''
                    }
                  />
                </Box>
              );
            })}
          </ContainerComponent>
        ) : (
          <>
            {!jobFeedStore?.jobfeed?.length &&
            jobFeedStore.loadMoreEnabled !== null ? (
              <NoJobsSaved />
            ) : (
              <Box
                sx={{
                  pt: 2.4,
                }}
              >
                {[...new Array(15)].map(index => {
                  return (
                    <Box display="flex" key={`jobfeed-skel-${index}`}>
                      <Skeleton
                        variant="rounded"
                        height={60}
                        sx={{
                          width: {xxs: '75px', md: '65px'},
                          mt: {xxs: 0, md: 1},
                          ml: 2,
                          mb: 1,
                        }}
                      />
                      <Box display="block" width="100%">
                        <Skeleton
                          variant="rounded"
                          width={'50%'}
                          height={20}
                          sx={{
                            mt: {xxs: 0, md: 1},
                            ml: {xxs: 2, md: 'none'},
                            mb: 1,
                          }}
                        />
                        <Skeleton
                          variant="rounded"
                          width={'90%'}
                          height={20}
                          sx={{
                            mt: {xxs: 0, md: 1},
                            ml: {xxs: 2, md: 'none'},
                            mb: 1,
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </Box>
      {!!jobFeedStore.loadMoreEnabled && !infiniteScroll && (
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            textAlign: 'center',
            mt: 5,
            justifyContent: 'center',
          }}
        >
          <LoadingButton
            loading={loadMoreLoading}
            variant="outlined"
            size="large"
            onClick={onLoadFeed}
            color="primary"
          >
            Load More
          </LoadingButton>
        </Box>
      )}
    </Card>
  );
};

export default ListWithTitle;
